import { isBoutiqueSide } from "@/features/invoices/invoice-utils";
import AppPanel from "@/features/invoices/order-confirmation-invoices/app-panel";
import useIdentity from "@/features/ui/hooks/use-identity";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
} from "@chakra-ui/react";
import useListDenseOrderConfirmationInvoices, {
  ListDenseOrderConfirmationInvoicesPathParameter,
  ListDenseOrderConfirmationInvoicesRequest,
} from "@/features/invoices/order-confirmation-invoices/hooks/use-list-dense-order-confirmation-invoices";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { orderConfirmationRowMapper } from "@/features/invoices/utils/order-confirmation-invoice-row-mapper";
import { OrderConfirmationInvoiceRow } from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice-row.type";
import useInvoiceGrid from "@/features/invoices/hooks/use-invoice-grid";
import AppGrid, {
  AppGridColumn,
  AppGridField,
  AppGridMeta,
} from "@/features/order-sheet-sets/app-grid";
import useAppGrid from "@/features/invoices/hooks/use-app-grid";
import GridRowIndicator from "@/features/order-sheet-sets/grid-row-indicator";
import IsolatedPanelSkeleton from "@/features/invoices/isolated-panel-skeleton";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import { GridRow } from "@/features/order-sheet-sets/helpers/app-grid-row-helper";
import { LightPayment } from "@/features/invoices/payment/payment.type";

interface IsolatedOrderConfirmationInvoicePanelProps {
  orderConfirmationInvoiceIds?: number[];
  title?: string;
  isCompact?: boolean;
  showNoRows?: boolean;
  setIsAddBankRemittanceReceiptOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setPayment?: React.Dispatch<React.SetStateAction<LightPayment | undefined>>;
  runMutation?: boolean | number;
  // 있으면 행을 눌렀을 때 상세 페이지로 가지 않고 이 함수로 수정 모달을 연다(프로포마 패널과 같다)
  onEdit?: (orderConfirmationInvoiceId: number) => void;
}

const rowMapper = orderConfirmationRowMapper.copy();

export default function IsolatedOrderConfirmationInvoicePanel({
  orderConfirmationInvoiceIds,
  title = "related_order_confirmation_invoices",
  isCompact = false,
  showNoRows = true,
  setIsAddBankRemittanceReceiptOpen = undefined,
  setPayment = undefined,
  runMutation = false,
  onEdit = undefined,
}: IsolatedOrderConfirmationInvoicePanelProps) {
  const identity = useIdentity();
  const { tTitle } = useI18nHelper();
  const { navigate } = useRouterHelper();
  const [rows, setRows] = useState<OrderConfirmationInvoiceRow[] | undefined>();
  // 바이어 입금 전에 부티크 송금 영수증을 먼저 올리려 할 때 확인받을 송금 요청(프로포마 패널과 같다)
  const [pendingBoutiquePayment, setPendingBoutiquePayment] = useState<LightPayment | undefined>(undefined);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const listOrderConfirmationInvoicesKey = useMemo(():
    | {
        parameter: ListDenseOrderConfirmationInvoicesPathParameter;
        request: ListDenseOrderConfirmationInvoicesRequest;
      }
    | undefined => {
    if (
      identity?.company?.type &&
      orderConfirmationInvoiceIds &&
      orderConfirmationInvoiceIds.length > 0
    ) {
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        request: {
          id__in: orderConfirmationInvoiceIds,
          pageNumber: 1,
          pageSize: orderConfirmationInvoiceIds.length,
          // 목록 칸(시즌·제목·디파짓·송금·상태·발행일)은 OC 응답만으로 그린다 — 오더·라인시트 조회 생략
          skipOrderSheetSets: true,
        },
      };
    }

    return undefined;
  }, [identity, orderConfirmationInvoiceIds]);

  const { error: showError, add: addAppToast } = useAppToasts();

  const {
    data: listOrderConfirmationInvoicesData,
    error: listOrderConfirmationInvoicesError,
    mutate: mutateListOrderConfirmationInvoices,
  } = useListDenseOrderConfirmationInvoices(
    listOrderConfirmationInvoicesKey?.parameter,
    listOrderConfirmationInvoicesKey?.request
  );

  useEffect(() => {
    if (runMutation) {
      mutateListOrderConfirmationInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runMutation]);


  useEffect(() => {
    if (listOrderConfirmationInvoicesData) {
      setRows((prev) => {
        return rowMapper.toRows(
          listOrderConfirmationInvoicesData.orderConfirmationInvoices.map(
            (ocInvoice) => {
              return {
                ...ocInvoice,
                isRead: false,
              };
            }
          ),
          prev
        );
      });
    }
  }, [listOrderConfirmationInvoicesData]);

  const appGrid = useAppGrid();
  const invoiceGrid = useInvoiceGrid({
    rows,
    mutateRows: mutateListOrderConfirmationInvoices,
  });

  const metas = useMemo((): AppGridMeta[] => {
    let pairs: { column: AppGridColumn; field: AppGridField }[] = [];

    if (identity?.company?.type === "BUYER") {
      pairs = [
        appGrid.pairs.season,
        // invoiceGrid.pairs.issuedBy,
        // invoiceGrid.pairs.issuedThrough,
        // invoiceGrid.pairs.orderedBy,
        invoiceGrid.pairs.title,
        invoiceGrid.pairs.deposit,
        invoiceGrid.pairs.remittanceStatus,
        // invoiceGrid.pairs.paidBy,
        invoiceGrid.pairs.issuedOn,
      ];
    } else if (identity?.company?.type === "AGENCY") {
      pairs = [
        appGrid.pairs.season,
        // invoiceGrid.pairs.issuedBy,
        // invoiceGrid.pairs.issuedThrough,
        // invoiceGrid.pairs.orderedBy,
        invoiceGrid.pairs.title,
        invoiceGrid.pairs.deposit,
        // invoiceGrid.pairs.buyerRemittedAmount,
        // invoiceGrid.pairs.agencyRemittedAmount,
        invoiceGrid.pairs.paymentRequiredAmount,
        invoiceGrid.pairs.remittanceStatus,
        // invoiceGrid.pairs.paidBy,
        invoiceGrid.pairs.issuedOn,
      ];
    } else if (identity?.company?.type === "BOUTIQUE") {
      pairs = [
        appGrid.pairs.season,
        // invoiceGrid.pairs.issuedBy,
        // invoiceGrid.pairs.issuedThrough,
        invoiceGrid.pairs.orderedBy,
        invoiceGrid.pairs.title,
        invoiceGrid.pairs.deposit,
        invoiceGrid.pairs.remittanceStatus,
        invoiceGrid.pairs.paidBy,
        invoiceGrid.pairs.issuedOn,
      ];
    }

    return [
      {
        rowType: "OrderConfirmationInvoice",
        gap: "8px",
        columns: pairs.map((pair) => pair.column),
        fields: pairs.map((pair) => pair.field),
        indicator: (row) => {
          return <GridRowIndicator type={"VIEW"} isHovered={row.isHovered} />;
        },
        onClick: (row) => {
          if (onEdit) {
            onEdit(row.id);
          } else {
            navigate(`/order-confirmation-invoices/${row.id}`);
          }
        },
        actions: [
          (_row: GridRow) => {
            const orderConfirmationInvoiceRow = _row as OrderConfirmationInvoiceRow;
            const targetPayment = orderConfirmationInvoiceRow.payments.find(payment => payment.sender.type === "BUYER" && payment.receiver.type === "AGENCY");
            return {
              name: "송금 영수증 업로드(To Agency)",
              isDisabled: orderConfirmationInvoiceRow.isRemittedToAgency,
              isLoading: false,
              onClick: () => {
                if (setIsAddBankRemittanceReceiptOpen) {
                  if (setPayment) {
                    if (targetPayment) {
                      setPayment(targetPayment);
                      setIsAddBankRemittanceReceiptOpen(true);
                    } else {
                      addAppToast({ status: "error", message: '해당 송금 요청을 찾을 수 없습니다. 관리자에게 문의하세요.' });
                    }
                  }
                }
              },
            };
          },
          (_row: GridRow) => {
            const orderConfirmationInvoiceRow = _row as OrderConfirmationInvoiceRow;
            const targetPayment = orderConfirmationInvoiceRow.payments.find(payment => payment.sender.type === "AGENCY" && isBoutiqueSide(payment.receiver.type));
            return {
              name: "송금 영수증 업로드(To Boutique)",
              isDisabled: orderConfirmationInvoiceRow.isRemittedToBoutique,
              isLoading: false,
              onClick: () => {
                if (setIsAddBankRemittanceReceiptOpen) {
                  if (setPayment) {
                    if (targetPayment && !orderConfirmationInvoiceRow.isRemittedToAgency) {
                      // 선송금 — 올리기 전에 확인을 받는다
                      setPendingBoutiquePayment(targetPayment);
                    } else if (targetPayment) {
                      setPayment(targetPayment);
                      setIsAddBankRemittanceReceiptOpen(true);
                    } else {
                      addAppToast({ status: "error", message: '해당 송금 요청을 찾을 수 없습니다. 관리자에게 문의하세요.' });
                    }
                  }
                }
              },
            };
          },
          (_row: GridRow) => {
            const orderConfirmationInvoiceRow = _row as OrderConfirmationInvoiceRow;
            let targetPayment = orderConfirmationInvoiceRow.payments.find(payment => payment.sender.type === "AGENCY" && payment.receiver.type === "BUYER");
            if (!targetPayment) {
              targetPayment = orderConfirmationInvoiceRow.payments.findLast(payment => payment.sender.type === "BUYER" && payment.receiver.type === "AGENCY");
            }
            return {
              name: "차액 송금 영수증 업로드",
              isDisabled: !(orderConfirmationInvoiceRow.isRemittedToAgency && orderConfirmationInvoiceRow.isRemittedToBoutique && !orderConfirmationInvoiceRow.isRemittedAdjustment),
              isLoading: false,
              onClick: () => {
                if (setIsAddBankRemittanceReceiptOpen) {
                  if (setIsAddBankRemittanceReceiptOpen) {
                    if (setPayment) {
                      if (targetPayment) {
                        setPayment(targetPayment);
                        setIsAddBankRemittanceReceiptOpen(true);
                      } else {
                        addAppToast({ status: "error", message: '차액 송금 요청을 찾을 수 없습니다. 관리자에게 문의하세요.' });
                      }
                    }
                  }
                }
              },
            };
          },
        ]
      },
    ];
  }, [addAppToast, appGrid.pairs.season, identity?.company?.type, invoiceGrid.pairs.paymentRequiredAmount, invoiceGrid.pairs.agencyRemittedAmount, invoiceGrid.pairs.buyerRemittedAmount, invoiceGrid.pairs.deposit, invoiceGrid.pairs.issuedOn, invoiceGrid.pairs.orderedBy, invoiceGrid.pairs.paidBy, invoiceGrid.pairs.remittanceStatus, invoiceGrid.pairs.title, navigate, onEdit, setIsAddBankRemittanceReceiptOpen, setPayment]);

  if (listOrderConfirmationInvoicesKey === undefined) {
    return null;
  } else if (rows == undefined) {
    return <IsolatedPanelSkeleton />;
  }

  return (
    <>
      <AppPanel
        title={tTitle(title)}
        variant={"ghost"}
        isCompact={isCompact}
      >
        <AppGrid isAnchored={false} rows={rows} metas={metas} showNoRows={showNoRows}/>
      </AppPanel>
      <AlertDialog
        isOpen={!!pendingBoutiquePayment}
        leastDestructiveRef={cancelRef}
        onClose={() => setPendingBoutiquePayment(undefined)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              선송금 처리 안내
            </AlertDialogHeader>
            <AlertDialogBody>
              바이어의 송금 영수증이 아직 업로드되지 않았습니다. 지금 부티크 해외 송금 영수증을 먼저 업로드하면 선송금 처리됩니다. 계속하시겠습니까?
            </AlertDialogBody>
            <AlertDialogFooter gap={"8px"}>
              <Button ref={cancelRef} onClick={() => setPendingBoutiquePayment(undefined)}>
                취소
              </Button>
              <Button
                colorScheme="red"
                onClick={() => {
                  if (pendingBoutiquePayment && setPayment && setIsAddBankRemittanceReceiptOpen) {
                    setPayment(pendingBoutiquePayment);
                    setIsAddBankRemittanceReceiptOpen(true);
                  }
                  setPendingBoutiquePayment(undefined);
                }}
              >
                확인
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
